import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useAuth } from '../context/AuthContext';
import { Printer, X, FileDown } from 'lucide-react';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../editor/nodes/PlaygroundNodes';
import { processMathEquation } from './utils/latexParser';
import { Modal, Button } from './common';
import PrintOverlayV4 from '../print/PrintOverlayV4';
import { orderPrintProblemData } from './utils/printProblemOrder';
import { createPrintDraftContext, printContextOf, printDraftScope } from '../print/printDrafts';
import usePrintSolutions from '../print/usePrintSolutions';
import { PRINT_BODY_BATCH, PRINT_MAX_PROBLEMS, chunkIds, printLimitMessage } from '../print/printRequestLimits';

// 인쇄 미리보기 = 신 인쇄(v4) 단독 노출. false로 바꾸면 구(v2) 미리보기 병행(종전)으로 복귀.
// 상세: docs/print/print-v4-default-entry.md
const PRINT_V4_ONLY = true;

// 시험지 미리보기 모달 컴포넌트 (재사용 가능)
export const PrintPreviewModal = (props) => {
  const { userId } = useAuth() || {};
  if (!props.isOpen) return null;
  // 다른 시험지 준비 중에 이전 문항과 새 설정이 함께 보이지 않도록 데이터 준비 상태도 분리한다.
  return <PrintPreviewContent key={printDraftScope(userId, printContextOf(props.testData)) || 'temporary'} {...props} />;
};

const PrintPreviewContent = ({
  isOpen, 
  onClose, 
  testData, 
  isMobileView = false 
}) => {
  const { getAccessToken } = useAuth();
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [printProblems, setPrintProblems] = useState([]);
  const [printFunction, setPrintFunction] = useState(null);
  const [getPrintHtmlFunction, setGetPrintHtmlFunction] = useState(null);
  const [printPreviewScale, setPrintPreviewScale] = useState(0.7);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPdfInput, setShowPdfInput] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');
  const [limitNotice, setLimitNotice] = useState(null); // 인쇄 상한을 넘었을 때 창 대신 보여 줄 글
  const [showV4, setShowV4] = useState(false); // v2 병행 모드(PRINT_V4_ONLY=false)에서만 사용
  const mountedRef = useRef(true); // 창을 닫으면 남은 본문 묶음을 보내지 않는다
  const previewRef = useRef(null);
  const containerRef = useRef(null);
  const [temporaryContext] = useState(createPrintDraftContext);
  const sourceContext = printContextOf(testData);
  const contextKind = sourceContext?.kind;
  const contextId = sourceContext?.id;
  const printContext = useMemo(() => contextKind ? { kind: contextKind, id: contextId } : temporaryContext,
    [contextKind, contextId, temporaryContext]);

  // 배율 조절 함수
  const handleScaleChange = useCallback((delta) => {
    setPrintPreviewScale(prev => {
      const newScale = prev + delta;
      return Math.max(0.2, Math.min(1.2, newScale));
    });
  }, []);

  // 배율 초기화 함수
  const resetScale = useCallback(() => {
    setPrintPreviewScale(0.7);
  }, []);

  // 모바일/데스크톱 뷰 변경 시 기본 배율 적용
  useEffect(() => {
    setPrintPreviewScale(0.7);
  }, [isMobileView]);

  // 기본 높이 측정 (1.0 스케일 기준)



  // 인쇄 처리 함수
  const handlePrint = useCallback(() => {
    if (printFunction) {
      printFunction();
    }
  }, [printFunction]);

  // 이미지 URL을 Base64 data URL로 변환하는 함수
  const convertImageToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('이미지 변환 실패:', url, error);
      return url; // 실패 시 원본 URL 반환
    }
  };

  // HTML 내 모든 이미지를 Base64로 변환
  const convertAllImagesToBase64 = async (html) => {
    const baseUrl = window.location.origin;

    // 1. 상대 경로를 절대 경로로 먼저 변환
    let processedHtml = html
      .replace(/url\(["']?(\/[^"')]+)["']?\)/g, `url("${baseUrl}$1")`)
      .replace(/src=["']?(\/[^"'\s>]+)["']?/g, `src="${baseUrl}$1"`)
      .replace(/href=["']?(\/[^"'\s>]+)["']?/g, `href="${baseUrl}$1"`);

    // 2. 이미지 URL 추출 (url() 및 src 속성)
    const imageUrls = new Set();

    // url() 패턴에서 이미지 URL 추출
    const urlPattern = /url\(["']?(https?:\/\/[^"')]+\.(?:png|jpg|jpeg|gif|svg|webp))["']?\)/gi;
    let match;
    while ((match = urlPattern.exec(processedHtml)) !== null) {
      imageUrls.add(match[1]);
    }

    // src 속성에서 이미지 URL 추출
    const srcPattern = /src=["'](https?:\/\/[^"']+\.(?:png|jpg|jpeg|gif|svg|webp))["']/gi;
    while ((match = srcPattern.exec(processedHtml)) !== null) {
      imageUrls.add(match[1]);
    }


    // 3. 각 이미지를 Base64로 변환
    for (const url of imageUrls) {
      const base64 = await convertImageToBase64(url);
      if (base64 !== url) { // 변환 성공한 경우만
        processedHtml = processedHtml.split(url).join(base64);
      }
    }

    // 4. 상대 경로(../)로 된 URL은 제거 (변환 불가)
    processedHtml = processedHtml.replace(/url\(["']?\.\.\/[^"')]+["']?\)/g, 'url("")');

    return processedHtml;
  };

  // PDF 다운로드 처리 함수
  const handleDownloadPdf = useCallback(async () => {
    if (isGeneratingPdf || printProblems.length === 0) return;
    if (!getPrintHtmlFunction) {
      alert('PDF 생성 준비가 완료되지 않았습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setIsGeneratingPdf(true);

    try {
      // PrintPreview에서 생성한 것과 동일한 HTML 가져오기
      const html = getPrintHtmlFunction();
      if (!html) {
        throw new Error('미리보기 HTML을 가져올 수 없습니다.');
      }

      // 이미지를 Base64로 변환
      const finalHtml = await convertAllImagesToBase64(html);

      // 인증 토큰 가져오기
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      }

      const defaultName = `${testData?.preset_id ? '프리셋' : '시험지'}_미리보기_${new Date().toISOString().slice(0, 10)}`;
      const filename = (pdfFilename.trim() || defaultName) + '.pdf';

      // HTML 문자열을 Blob으로 변환하여 파일로 전송 (multipart/form-data)
      const htmlBlob = new Blob([finalHtml], { type: 'text/html;charset=utf-8' });
      const htmlSizeMb = (htmlBlob.size / (1024 * 1024)).toFixed(2);
      console.log(`[PDF] generate-upload request: html=${htmlSizeMb}MB`);
      const htmlFile = new File([htmlBlob], 'document.html', { type: 'text/html' });

      const formData = new FormData();
      formData.append('html_file', htmlFile);
      formData.append('filename', filename);
      formData.append('encoding', 'utf-8');

      // PDF API 호출 (multipart/form-data 방식)
      const requestStartedAt = performance.now();
      let response;
      try {
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/pdfs/generate-upload`, {
          method: 'POST',
          headers: {
            'Authorization': token,
            // Content-Type은 FormData 사용 시 자동 설정됨 (boundary 포함)
          },
          body: formData,
        });
      } catch (fetchError) {
        const requestDurationMs = performance.now() - requestStartedAt;
        console.error(
          `[PDF] generate-upload fetch failed: durationMs=${Math.round(requestDurationMs)}`,
          fetchError
        );
        throw fetchError;
      }
      const requestDurationMs = performance.now() - requestStartedAt;
      console.log(`[PDF] generate-upload response: status=${response.status} durationMs=${Math.round(requestDurationMs)}`);

      // 429 Too Many Requests 처리
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        throw new Error(`서버가 바쁩니다. ${retryAfter ? `${retryAfter}초 후` : '잠시 후'} 다시 시도해주세요.`);
      }

      if (!response.ok) {
        const requestId = response.headers.get('X-Request-Id');
        const contentType = response.headers.get('content-type') || '';
        let serverMessage = '';

        if (contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => ({}));
          serverMessage = errorData.message || '';
        } else {
          const errorText = await response.text().catch(() => '');
          serverMessage = errorText ? errorText.slice(0, 300) : '';
        }

        console.error('[PDF] generate-upload failed', {
          status: response.status,
          requestId,
          contentType,
          serverMessage,
        });

        throw new Error(
          serverMessage ||
            `PDF 생성 실패: ${response.status}${requestId ? ` (X-Request-Id: ${requestId})` : ''}`
        );
      }

      // JSON 응답 파싱 (S3 presigned URL 방식)
      const result = await response.json();

      // 경고 확인
      if (result.data?.warns && result.data.warns.length > 0) {
        console.warn('PDF 생성 경고:', result.data.warns);
      }

      // download_url 확인
      const downloadUrl = result.data?.download_url;
      if (!downloadUrl) {
        throw new Error('다운로드 URL을 받지 못했습니다.');
      }

      console.log('[PDF] S3 다운로드 URL 수신:', {
        expires_in: result.data?.expires_in,
        request_id: result.data?.request_id,
      });

      // S3 presigned URL로 다운로드 (백엔드 권장 방식)
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();

    } catch (error) {
      console.error('PDF 다운로드 오류:', error);
      alert(`PDF 다운로드 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [isGeneratingPdf, printProblems.length, testData, getPrintHtmlFunction, getAccessToken, pdfFilename]);

  // 토큰 갱신 함수
  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${url}`, {
        ...options,
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API 호출 실패:', error);
      throw error;
    }
  }, [getAccessToken]);

  // 시험지 미리보기 데이터 준비
  const handlePrintExam = useCallback(async () => {
    if (!testData || !(testData.problems_list || testData.sequence || testData.preset_id)) {
      alert('시험 문제 데이터가 없습니다.');
      return;
    }
    
    try {
      setIsPrintLoading(true);
      setLimitNotice(null);

      // 상한을 넘으면 본문을 받기 전에 멈춘다 — 어차피 못 여는 창 때문에 서버에 요청을 보내지 않는다
      const requestedCount = (testData.problems_list || testData.sequence || []).length;
      if (requestedCount > PRINT_MAX_PROBLEMS) {
        setLimitNotice(printLimitMessage(requestedCount));
        return;
      }

      // preset_id가 있으면 새 엔드포인트 사용
      if (testData.preset_id) {
        const response = await fetchWithTokenRefresh(
          `/api/exams/presets/print?preset_id=${testData.preset_id}`
        );
        
        if (!response?.data) {
          throw new Error('프리셋 데이터를 불러오는데 실패했습니다.');
        }
        
        // 문제 순서를 유지하기 위해 problems_list의 순서대로 처리
        const orderedProblemIds = testData.problems_list.map(p => p.problem_id);
        
        // 새 엔드포인트의 응답 형식에 맞춰 데이터 변환
        const problemList = await Promise.all(orderedProblemIds.map(async (problemId) => {
          const problemData = response.data[problemId];
          
          if (!problemData?.prob_lexical) {
            throw new Error(`문제 ${problemId}의 데이터가 올바르지 않습니다.`);
          }
          
          const editor = createEditor({
            nodes: [...PlaygroundNodes]
          });
          
          const processNode = (node) => {
            if (node.type === 'equation') {
              node.equation = processMathEquation(node.equation);
            } else if (node.type === 'box-content') {
              node.className = 'PlaygroundEditorTheme__boxContent';
            }
            if (node.children && Array.isArray(node.children)) {
              node.children = node.children.map(child => processNode(child));
            }
            return node;
          };
          
          const lexicalData = problemData.prob_lexical;
          lexicalData.root = processNode(lexicalData.root);
          const editorState = editor.parseEditorState(lexicalData);
          
          let content = '';
          editor.setEditorState(editorState);
          editor.update(() => {
            content = $generateHtmlFromNodes(editor);
          });
          
          return {
            problem_id: problemId,
            content,
            answer: problemData.answer // 답안도 함께 저장
          };
        }));
        
        setPrintProblems(problemList);
        return; // preset 처리 완료 후 함수 종료
      }
      
      // 기존 로직 (preset_id가 없을 때)
      // 문제 ID 추출
      let problemIds = [];
      if (testData.problems_list) {
        problemIds = testData.problems_list.map(p => p.problem_id);
      } else if (testData.sequence) {
        problemIds = testData.sequence.map(problem => {
          if (Array.isArray(problem)) {
            return problem[0];
          }
          return problem.problem_id || problem;
        });
      }

      if (problemIds.length === 0) {
        alert('문제 ID를 찾을 수 없습니다.');
        return;
      }

      // 서버 제한만큼 나눠 차례로 받는다. 그 이하면 종전과 같은 요청 한 번이다.
      const bodyRows = [];
      for (const ids of chunkIds(problemIds, PRINT_BODY_BATCH)) {
        const response = await fetchWithTokenRefresh(
          `/api/problems/print?problem_ids=${ids.join(',')}`
        );

        if (!response?.data) {
          throw new Error('문제 데이터를 불러오는데 실패했습니다.');
        }
        if (!mountedRef.current) return;
        if (Array.isArray(response.data)) bodyRows.push(...response.data);
      }

      // IN 조회 응답 순서를 시험지 순서로 믿지 않는다. 요청 순서로 되놓고, 한 문제라도 빠졌으면
      // 불완전한 시험지를 인쇄하지 않는다(삭제·권한 범위 밖 문항을 조용히 생략하는 것도 금지).
      const { ordered, missing } = orderPrintProblemData(bodyRows, problemIds);
      if (missing.length > 0) {
        const shown = missing.slice(0, 5).map((id) => `#${id}`).join(', ');
        const more = missing.length > 5 ? ` 외 ${missing.length - 5}개` : '';
        throw new Error(`문제 본문을 받지 못했습니다 (${shown}${more}).`);
      }

      // 문제 데이터 구조화
      const problemList = await Promise.all(ordered.map(async (problem) => {
        if (!problem?.lexical_data?.lexical_data) {
          throw new Error(`문제 ${problem.problem_id}의 데이터가 올바르지 않습니다.`);
        }

        const editor = createEditor({
          nodes: [...PlaygroundNodes]
        });

        const processNode = (node) => {
          if (node.type === 'equation') {
            node.equation = processMathEquation(node.equation);
          } else if (node.type === 'box-content') {
            node.className = 'PlaygroundEditorTheme__boxContent';
          }
          if (node.children && Array.isArray(node.children)) {
            node.children = node.children.map(child => processNode(child));
          }
          return node;
        };

        const lexicalData = problem.lexical_data.lexical_data;
        lexicalData.root = processNode(lexicalData.root);
        const editorState = editor.parseEditorState(lexicalData);

        let content = '';
        editor.setEditorState(editorState);
        editor.update(() => {
          content = $generateHtmlFromNodes(editor);
        });

        return {
          problem_id: problem.problem_id,
          content
        };
      }));

      setPrintProblems(problemList);
    } catch (error) {
      console.error('시험지 준비 실패:', error);
      alert('시험지를 준비하는 중 오류가 발생했습니다.');
    } finally {
      setIsPrintLoading(false);
    }
  }, [testData, fetchWithTokenRefresh]);

  // 모달이 열릴 때 시험지 데이터 준비
  useEffect(() => {
    if (isOpen && testData) {
      handlePrintExam();
    }
  }, [isOpen, testData, handlePrintExam]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // 풀이 데이터 — 오답노트 v4 어댑터와 같은 훅을 쓴다
  const {
    solutions: solutionData,
    loading: solutionsLoading,
    failedCount: solutionsFailedCount,
    wanted: showSolutions,
    setWanted: setShowSolutions,
    retry: retrySolutions,
  } = usePrintSolutions(printProblems, fetchWithTokenRefresh);

  if (!isOpen) return null;

  // v4 오버레이 공통 메타 — v4 단독/병행 두 렌더 경로가 동일 값 사용
  const v4Meta = {
    tree_name: testData?.tree_name || '',
    edu_ver: testData?.edu_ver || '',
    student_name: testData?.student_name || '',
    exam_id: testData?.exam_id || '',
    customName: testData?.preset_name || '', // 커스텀 이름(프리셋) — 없으면 부제 생략

    leftFooter: `카이테스트 · ${
      testData?.exam_type === 'alpha' ? 'α' :
      testData?.exam_type === 'beta' ? 'β' :
      testData?.exam_type === 'gamma' ? 'γ' :
      testData?.exam_type === 'delta' ? 'δ' :
      testData?.exam_type === 'epsilon' ? 'ε' :
      testData?.exam_type || ''
    }`,
  };

  // v4 단독 노출(2026-08-14 결정 — docs/print/print-v4-default-entry.md):
  // 구(v2) 미리보기 UI는 렌더하지 않고 신 인쇄만 띄운다. v4를 닫으면 모달 전체가 닫힌다.
  // 데이터 파이프라인(handlePrintExam→printProblems, showSolutions→solutionData)은 컴포넌트
  // 레벨 effect라 v2 UI 없이 동일 동작. v2 병행(종전)으로 복귀하려면 이 플래그만 false로.
  if (PRINT_V4_ONLY) {
    return createPortal(
      <>
        {printProblems.length === 0 && (
          <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999980 }}>
            <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
            <div className="relative z-10 flex items-center gap-3 rounded-2xl bg-white px-6 py-4 shadow-lg">
              {isPrintLoading ? (
                <>
                  <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                  <span className="text-sm text-gray-600">시험지 준비 중...</span>
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-600">{limitNotice || '인쇄할 문제가 없습니다.'}</span>
                  <button onClick={onClose} className="rounded-full border border-gray-300 px-3 py-1 text-xs hover:bg-gray-100">
                    닫기
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        <PrintOverlayV4
          printContext={printContext}
          open={printProblems.length > 0}
          onClose={onClose}
          problems={printProblems}
          solutions={solutionData}
          solutionsLoading={solutionsLoading}
          solutionsFailedCount={solutionsFailedCount}
          onRetrySolutions={retrySolutions}
          onEnsureSolutions={() => setShowSolutions(true)}
          meta={v4Meta}
        />
      </>,
      document.body
    );
  }

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center" 
      style={{ 
        zIndex: 9999980,
        paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
        paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px'
      }}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      
      <div 
        className="relative z-10 flex w-[95vw] flex-col overflow-hidden rounded-3xl bg-white shadow-lg lg:w-[960px]"
        style={{
          height: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh',
          maxHeight: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
        }}
      >
        {/* 헤더 */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-3xl border-b bg-white p-4">
          <div className="flex items-center">
            <h1 className="text-xl font-bold text-gray-800">
              {testData?.preset_id ? '프리셋 미리보기' : '시험지 미리보기'}
            </h1>
          </div>
          <div className="flex items-center">
            {/* 풀이 옵션 */}
            <label className="mr-4 flex cursor-pointer items-center space-x-1.5">
              <input
                type="checkbox"
                checked={showSolutions}
                onChange={(e) => setShowSolutions(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-700">풀이 포함</span>
            </label>
            {/* 축소/확대/초기화 버튼들 */}
            <div className="mr-4 flex items-center gap-2">
              <Button
                variant="info"
                size="xs"
                onClick={() => handleScaleChange(-0.1)}
                disabled={printPreviewScale <= 0.2}
                title="축소 (10%)"
                className="px-2"
              >
                −
              </Button>
              <span className="min-w-[40px] rounded bg-gray-100 px-2 py-1 text-center text-xs font-medium">
                {Math.round(printPreviewScale * 100)}%
              </span>
              <Button
                variant="info"
                size="xs"
                onClick={() => handleScaleChange(0.1)}
                disabled={printPreviewScale >= 1.2}
                title="확대 (10%)"
                className="px-2"
              >
                +
              </Button>
              <Button
                variant="secondary"
                size="xs"
                onClick={resetScale}
                title="기본 크기로 초기화"
                className="px-2"
              >
                초기화
              </Button>
            </div>
            
            {/* PDF 다운로드 버튼 */}
            <div className="relative mr-2">
              <button
                onClick={() => setShowPdfInput(prev => !prev)}
                disabled={isGeneratingPdf || printProblems.length === 0}
                className="rounded-full p-1 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                title="PDF 다운로드"
              >
                {isGeneratingPdf ? (
                  <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                ) : (
                  <FileDown size={20} />
                )}
              </button>
              {showPdfInput && (
                <div className="absolute right-0 top-full z-20 mt-1 flex items-center gap-1 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-200">
                  <input
                    type="text"
                    value={pdfFilename}
                    onChange={(e) => setPdfFilename(e.target.value)}
                    placeholder={`${testData?.preset_id ? '프리셋' : '시험지'}_미리보기`}
                    className="w-48 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    onKeyDown={(e) => { if (e.key === 'Enter') { handleDownloadPdf(); setShowPdfInput(false); } }}
                    autoFocus
                  />
                  <span className="text-xs text-gray-400">.pdf</span>
                  <button
                    onClick={() => { handleDownloadPdf(); setShowPdfInput(false); }}
                    disabled={isGeneratingPdf}
                    className="whitespace-nowrap rounded-lg bg-primary px-3 py-1 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    생성
                  </button>
                </div>
              )}
            </div>

            {/* 신 인쇄(실측 A4/B4) — v2와 병행(과도기). 용지 선택은 오버레이 안에서 */}
            <button
              onClick={() => setShowV4(true)}
              disabled={printProblems.length === 0}
              className="mr-2 flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
              title="실측 규격(A4/B4) 신 인쇄 — 브라우저에서 인쇄/PDF 저장"
            >
              <Printer size={14} />
              새 인쇄(β)
            </button>

            {/* 인쇄 버튼 */}
            <button onClick={handlePrint} className="mr-2 rounded-full p-1 hover:bg-gray-200">
              <Printer size={20} />
            </button>

            {/* 닫기 버튼 */}
            <button onClick={onClose} className="rounded-full border border-gray-300 p-1 hover:bg-gray-200">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* 컨텐츠 영역 */}
        <div className="flex-1 bg-gray-200" style={{ overflow: 'scroll' }}>
          {isPrintLoading ? (
            <div className="flex flex-1 items-center justify-center bg-gray-200">
              <div className="text-center">
                <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-primary"></div>
                <p className="text-gray-600">시험지를 준비하는 중...</p>
              </div>
            </div>
          ) : (
            <div
              ref={containerRef}
              className="min-h-0 flex-1 overflow-auto bg-gray-200 p-4"
            >
              <div ref={previewRef}>
                <PrintPreview
                  problems={printProblems}
                  mode="preview"
                  scale={printPreviewScale}
                  tree_name={testData?.tree_name || ''}
                  edu_ver={testData?.edu_ver || ''}
                  student_name={testData?.student_name || ''}
                  exam_id={testData?.exam_id || ''}
                  leftFooter={`카이테스트 · ${
                    testData?.exam_type === 'alpha' ? 'α' :
                    testData?.exam_type === 'beta' ? 'β' :
                    testData?.exam_type === 'gamma' ? 'γ' :
                    testData?.exam_type === 'delta' ? 'δ' :
                    testData?.exam_type === 'epsilon' ? 'ε' :
                    testData?.exam_type || ''
                  }`}
                  rightFooter={`${testData?.edu_ver || ''} · ${testData?.tree_name || ''}`}
                  onPrint={setPrintFunction}
                  onGetPrintHtml={setGetPrintHtmlFunction}
                  solutions={solutionData}
                />
              </div>
            </div>
          )}
        </div>

        {/* 실측 규격(A4/B4) 신 인쇄 오버레이 — 데이터 계약은 v2와 동일(Lexical→HTML 결과 재사용) */}
        <PrintOverlayV4
          printContext={printContext}
          open={showV4}
          onClose={() => setShowV4(false)}
          problems={printProblems}
          solutions={solutionData}
          solutionsLoading={solutionsLoading}
          solutionsFailedCount={solutionsFailedCount}
          onRetrySolutions={retrySolutions}
          onEnsureSolutions={() => setShowSolutions(true)}
          meta={v4Meta}
        />
      </div>
    </div>,
    document.body
  );
};

// PrintPreview 컴포넌트

const PrintPreview = ({
  problems = [],
  mode = 'preview',
  scale = 1,
  onProblemClick = null,
  tree_name = '교재 미리보기',
  edu_ver = '중등 수학',
  student_name = '테스트',
  exam_id = '1234',
  leftFooter = 'SKY 미래연수학학원',
  rightFooter = '오른쪽 푸터',
  onPrint,
  onGetPrintHtml,
  editingProblemId = null,
  solutions = []
}) => {
  const [answers, setAnswers] = React.useState([]);
  // 정답 결과를 좌우하는 것은 '어떤 문항이 어떤 순서로 있는가'와 '본문에 answer 가 실려 있는가'뿐이다
  const answerKey = React.useMemo(
    () => problems.map((p) => `${p?.problem_id}:${p?.answer !== undefined ? 1 : 0}`).join(','),
    [problems],
  );
  const solMeasureRef = React.useRef(null);
  const solColRef = React.useRef(null);
  const solContentRef = React.useRef(null);
  const [solLayout, setSolLayout] = React.useState({ pages: [], positions: [] });
  const [solColPx, setSolColPx] = React.useState(0); // 실제 렌더된 칼럼 높이(px)

  React.useEffect(() => {
    const fetchAnswers = async () => {
      // 문제 데이터에 이미 답안이 포함되어 있는지 확인 (preset인 경우)
      const hasAnswersInProblems = problems.some(p => p.answer !== undefined);
      
      if (hasAnswersInProblems) {
        // preset에서 온 데이터인 경우 답안이 이미 포함되어 있음
        const sortedAnswers = problems.map((problem, index) => {
          let processedAnswer = '-';
          
          if (problem.answer) {
            try {
              const tempDiv = document.createElement('div');
              katex.render(processMathEquation(problem.answer), tempDiv, {
                throwOnError: false,
                displayMode: true,
                strict: false
              });
              processedAnswer = tempDiv.innerHTML;
            } catch (error) {
              console.error('KaTeX 렌더링 오류:', error);
              processedAnswer = processMathEquation(problem.answer);
            }
          }
          
          return {
            number: index + 1,
            problem_id: problem.problem_id,
            answer: processedAnswer
          };
        });
        setAnswers(sortedAnswers);
        return;
      }
      
      // 기존 로직 (답안을 별도로 가져오는 경우)
      try {
        const token = localStorage.getItem('accessToken');
        const problemIds = problems.map(p => p.problem_id).join(',');
        
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/solutions/answers?problem_ids=${problemIds}`,
          {
            headers: { 'Authorization': token }
          }
        );
        const responseData = await response.json();
        
        if (responseData.status === 'success') {
          // KaTeX로 정답 처리
          const sortedAnswers = problems.map((problem, index) => {
            const answerData = responseData.data.find(a => a.problem_id === problem.problem_id);
            let processedAnswer = '-';
            
            if (answerData?.answer) {
              try {
                const tempDiv = document.createElement('div');
                katex.render(processMathEquation(answerData.answer), tempDiv, {
                  throwOnError: false,
                  displayMode: true,
                  strict: false
                });
                processedAnswer = tempDiv.innerHTML;
              } catch (error) {
                console.error('KaTeX 렌더링 오류:', error);
                processedAnswer = processMathEquation(answerData.answer);
              }
            }

            return {
              number: index + 1,
              problem_id: problem.problem_id,
              answer: processedAnswer
            };
          });
          setAnswers(sortedAnswers);
        }
      } catch (error) {
        console.error('답안 데이터 가져오기 실패:', error);
        setAnswers([]); // 에러 발생 시 답안 초기화
      }
    };

    if (problems.length > 0) {
      fetchAnswers();
    } else {
      setAnswers([]); // problems가 비어있으면 답안 초기화
    }
    // 참조가 아니라 값으로 잰다 — 실시간 미리보기(제작 창)는 순서만 바꿔도 problems 배열이
    // 새로 만들어지므로, [problems] 로 두면 클릭 한 번마다 전 문항 정답을 다시 요청한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerKey]);

  // 높이 계산 함수를 컴포넌트 내부로 이동
  const calculateProblemHeight = (problem, index) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: 488px;
      left: -9999px;
      display: block;
      opacity: 0;
    `;

    tempDiv.innerHTML = `
      <div class="problem-item" style="display: block; position: static;">
        <div class="problem-number" style="display: block;">${String(index + 1).padStart(2, '0')}</div>
        <div class="problem-content" style="display: block;">${problem.content}</div>
      </div>
    `;

    document.body.appendChild(tempDiv);
    const height = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);

    return height / 3.779527559055118;
  };

  const distributeProblems = (problems) => {
    let pages = [];
    let currentPage = { left: [], right: [] };
    let currentPageIndex = 0;
    let problemCounter = 0;

    const getPositions = (pageIndex) => {
      if (pageIndex === 0) {
        return {
          startHeight: 0,
          midHeight: 160,
          singleAreaHeight: 160
        };
      }
      return {
        startHeight: 0,
        midHeight: 205,
        singleAreaHeight: 205
      };
    };

    const processPage = (problem, index) => {
      const { startHeight, midHeight, singleAreaHeight } = getPositions(currentPageIndex);
      const problemHeight = calculateProblemHeight(problem, index);

      // 페이지가 가득 찼는지 먼저 확인
      const isPageFull = 
        (currentPage.left.length === 2 && currentPage.right.length === 2) || 
        (currentPage.left.length === 1 && currentPage.left[0]?.useFullColumn && currentPage.right.length === 2) ||
        (currentPage.right.length === 1 && currentPage.right[0]?.useFullColumn);

      if (isPageFull) {
        pages.push({ ...currentPage });
        currentPage = { left: [], right: [] };
        currentPageIndex++;
        return processPage(problem, index);
      }

      if (problemHeight > singleAreaHeight) {
        const longProblem = {
          ...problem,
          startHeight: 0,
          height: problemHeight,
          useFullColumn: true,
          area: 'full',
          problemNumber: ++problemCounter
        };

        // 긴 문제 배치 로직
        if (currentPage.left.length === 0 && currentPage.right.length === 0) {
          // 페이지가 비어있으면 왼쪽에 배치
          longProblem.column = 'left';
          currentPage.left = [longProblem];
        } else if (currentPage.left.length >= 1 && currentPage.right.length === 0) {
          // 왼쪽에 문제가 있고 오른쪽이 비어있으면 오른쪽에 배치
          longProblem.column = 'right';
          currentPage.right = [longProblem];
        } else {
          // 그 외의 경우(오른쪽에 문제가 있거나, 왼쪽이 비어있고 오른쪽에 문제가 있는 경우)
          // 새 페이지의 왼쪽에 배치
          pages.push({ ...currentPage });
          currentPage = { 
            left: [{ ...longProblem, column: 'left' }], 
            right: [] 
          };
          currentPageIndex++;
        }
        return;
      }

      const addProblemToPage = (column) => {
        const isTop = column === 'left' ? currentPage.left.length === 0 : currentPage.right.length === 0;
        const height = isTop ? startHeight : midHeight;

        const normalProblem = {
          ...problem,
          startHeight: height,
          height: problemHeight,
          problemNumber: ++problemCounter,
          column: column
        };

        if (column === 'left') {
          currentPage.left.push(normalProblem);
        } else {
          currentPage.right.push(normalProblem);
        }
      };

      // 일반 문제 배치 로직
      if (currentPage.left.length === 1 && currentPage.left[0].useFullColumn) {
        // 왼쪽에 긴 문제가 있으면 오른쪽에만 배치
        if (currentPage.right.length < 2) {
          addProblemToPage('right');
        }
      } else if (currentPage.right.length === 1 && currentPage.right[0].useFullColumn) {
        // 오른쪽에 긴 문제가 있으면 새 페이지로
        pages.push({ ...currentPage });
        currentPage = { left: [], right: [] };
        currentPageIndex++;
        return processPage(problem, index);
      } else {
        // 일반적인 경우의 배치 로직
        if (currentPage.left.length < 2) {
          addProblemToPage('left');
        } else if (currentPage.right.length < 2) {
          addProblemToPage('right');
        }
      }
    };

    problems.forEach((problem, index) => {
      processPage(problem, index);
    });

    if (currentPage.left.length > 0 || currentPage.right.length > 0) {
      pages.push(currentPage);
    }

    return pages;
  };

  // 재조판은 비싸다 — 문항마다 임시 div 를 body 에 붙여 높이를 재므로 문항 수만큼 강제 레이아웃이
  // 일어난다. scale·editingProblemId 는 CSS 만 바뀌는 값이라 여기까지 다시 돌 이유가 없다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pages = React.useMemo(() => distributeProblems(problems), [problems]);

  // 문제 페이지가 홀수이면 빈 페이지 1장 자동 추가 (양면인쇄 시 문제/정답 분리)
  const combinedPages = React.useMemo(() => {
    const result = pages.map((page) => ({ type: 'content', content: page }));
    if (pages.length % 2 !== 0) {
      result.push({ type: 'empty' });
    }
    return result;
  }, [pages]);

  // 풀이: 실제 칼럼 너비에 맞춰 높이 측정 → 슬라이스 계산
  React.useEffect(() => {
    if (solutions.length === 0) {
      setSolLayout({ pages: [], positions: [] });
      return;
    }

    const calculate = () => {
      const measureDiv = solMeasureRef.current;
      const colDiv = solColRef.current;
      if (!measureDiv) return;

      // 실제 칼럼 너비 측정 (scale 보정: getBoundingClientRect는 transform 영향 받음)
      const s = scale || 1;
      let colWidth = 533;
      if (colDiv) {
        colWidth = colDiv.getBoundingClientRect().width / s;
      }

      // 측정 div 너비를 실제 칼럼 너비에 맞춤
      measureDiv.style.width = `${colWidth}px`;

      // 강제 리플로우 후 재측정
      void measureDiv.offsetHeight;

      const items = measureDiv.querySelectorAll('[data-sol-idx]');
      if (items.length === 0) return;

      const MM = 3.779527559055118;
      const COL_H = solColPx > 0 ? solColPx : 370 * MM;
      const FADE = 52; // 그라데이션 높이 (약 2줄, px)
      const STEP = COL_H - FADE; // 슬라이스 간격: 그라데이션이 가린 만큼 다음 칼럼이 앞서 시작

      const positions = [];
      let y = 0;
      let hasZero = false;

      items.forEach((el) => {
        const idx = Number(el.getAttribute('data-sol-idx'));
        const sol = solutions[idx];
        if (!sol) return;
        const h = el.getBoundingClientRect().height / s;
        if (h < 1) hasZero = true; // 아직 렌더 안 된 아이템
        const mb = parseFloat(window.getComputedStyle(el).marginBottom) || 0;
        const totalH = h + mb;
        positions.push({ sol, y, h: totalH });
        y += totalH;
      });

      // 높이 0인 아이템이 있으면 아직 렌더 중 → 건너뛰고 재시도 대기
      if (hasZero) return;

      const totalH = y;
      const totalChunks = Math.ceil(totalH / STEP);
      const totalPages = Math.ceil(totalChunks / 2);

      const pages = [];
      for (let p = 0; p < totalPages; p++) {
        const cols = [0, 1].map((c) => {
          const chunkIdx = p * 2 + c;
          const start = chunkIdx * STEP; // STEP 간격으로 시작
          const end = start + COL_H;     // COL_H 높이만큼 표시
          const visible = positions.filter(s => s.y + s.h > start && s.y < end);
          return { start, visible };
        });
        pages.push(cols);
      }

      setSolLayout({ pages, positions });
    };

    // 초기 계산
    const timer1 = setTimeout(() => requestAnimationFrame(calculate), 500);

    // 이미지 로드 후 재계산
    const timer2 = setTimeout(() => {
      const measureDiv = solMeasureRef.current;
      if (!measureDiv) return;
      const images = measureDiv.querySelectorAll('img');
      if (images.length === 0) return;
      let loaded = 0;
      const onLoad = () => {
        loaded++;
        if (loaded >= images.length) {
          requestAnimationFrame(calculate);
        }
      };
      images.forEach((img) => {
        if (img.complete) {
          loaded++;
        } else {
          img.addEventListener('load', onLoad, { once: true });
          img.addEventListener('error', onLoad, { once: true });
        }
      });
      if (loaded >= images.length) {
        requestAnimationFrame(calculate);
      }
    }, 800);

    // 안전장치: 2초 후 한번 더 계산
    const timer3 = setTimeout(() => requestAnimationFrame(calculate), 2000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [solutions, solColPx]);

  // 풀이 콘텐츠 영역 렌더 후 실제 높이 측정
  React.useEffect(() => {
    if (solLayout.pages.length === 0) return;

    requestAnimationFrame(() => {
      const el = solContentRef.current;
      const colEl = solColRef.current;
      if (!el) return;
      // scale 보정하여 실제 레이아웃 높이 측정
      const s = scale || 1;
      const h = el.getBoundingClientRect().height / s;
      if (h > 0 && solColPx === 0) {
        setSolColPx(h);
      }
    });
  }, [solLayout.pages.length, solColPx]);

  // 풀이: 넘치는 수식만 font-size 축소 (레이아웃 크기도 함께 줄어듦)
  React.useEffect(() => {
    if (solLayout.pages.length === 0) return;

    const shrinkOverflow = () => {
      // 블록 수식 + 인라인 수식 모두 처리
      document.querySelectorAll('.solution-page .solution-content .katex-display, .solution-page .solution-content .katex-html').forEach((el) => {
        // 초기화
        el.style.fontSize = '';

        const parent = el.closest('.solution-content') || el.closest('[style*="flex"]');
        if (!parent) return;
        const maxW = parent.clientWidth;
        const elW = el.scrollWidth;

        if (elW > maxW && elW > 0) {
          const currentSize = parseFloat(window.getComputedStyle(el).fontSize) || 18;
          // 0.92 여유분으로 확실히 칼럼 안에 들어가도록
          el.style.fontSize = `${currentSize * (maxW / elW) * 0.92}px`;
        }
      });
    };

    const timer = setTimeout(shrinkOverflow, 600);
    return () => clearTimeout(timer);
  }, [solLayout.pages]);

  const containerStyle = {
    ...(mode === 'preview' ? {
      zoom: scale,
    } : {}),
  };

  const answerPageStyles = {
    answersTable: {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '0',
      padding: '0',
      flex: 'none',
      overflow: 'hidden',
      height: 'fit-content',
      boxSizing: 'border-box',
      maxHeight: '205mm', // A4 높이에서 여백과 헤더/푸터 공간을 뺀 값
      pageBreakInside: 'auto' // 페이지 나누기 허용
    },
    tableRow: {
      position: 'relative',
      width: '100%',
      height: '55px',
      pageBreakInside: 'avoid', // 행 중간에서 페이지 나누기 방지
      breakInside: 'avoid',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '1px',
        background: '#E5E5E5'
      }
    },
    tableHeader: {
      padding: '0px 5mm',
      textAlign: 'center',
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#666',
      height: '30px'
    },
    tableCell: {
      padding: '6px 5mm',
      textAlign: 'center',
      height: '55px',
      lineHeight: '1.2',
      verticalAlign: 'middle',
      maxHeight: '55px',
      whiteSpace: 'nowrap'
    },
    numberCell: {
      fontSize: '20px',
      fontWeight: '900',
      lineHeight: '1',
      color: '#4ABC85',
      textAlign: 'left',
      paddingLeft: '0',
      paddingRight: '0',
      width: '80px'
    },
    tidCell: {
      fontSize: '16px',
      fontWeight: '200',
      lineHeight: '1',
      width: '80px'
    },
    answerCell: {
      textAlign: 'left',
      minHeight: '55px',
      height: '55px',
      maxHeight: '55px',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      whiteSpace: 'normal',
      padding: '6px 5mm'
    },
    headerLine: {
      border: 'none',
      borderTop: '1px solid #231F20',
      margin: '20px 0',
      width: '100%'
    },
    logo: {
      width: '100px',
      height: '25px',
      backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/logo_header_solution.png)`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      margin: '0',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      colorAdjust: 'exact'
    },
    examId: {
      fontSize: '15px',
      fontFamily: 'Pretendard, sans-serif',
      fontWeight: '700',
      color: '#333',
      margin: '0'
    },
    topRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '3mm',
      marginBottom: '2mm',
      marginTop: '30px'
    },
    bottomRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px'
    },
    title: {
      color: '#4ABC85',
      fontSize: '36px',
      fontFamily: 'Pretendard, sans-serif',
      fontWeight: '900',
      margin: '0'
    },
    printHeader: {
      marginBottom: '20px'
    },
    printHeaderRest: {
      position: 'relative',
      marginBottom: '20px',
      padding: '30px 15mm 0 15mm',
      background: `url(${process.env.PUBLIC_URL}/assets/images/header_solution.png) top center/100% 30px no-repeat`,
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      colorAdjust: 'exact'
    },
    headerContent: {
      width: '100%',
      position: 'relative',
      zIndex: 1
    },
    answerContainer: {
      padding: '0 15mm',
      width: '100%',
      boxSizing: 'border-box'
    }
  };

  // 한 페이지에 들어갈 수 있는 최대 행 수 계산
  const ANSWER_ROW_HEIGHT = 55; // 각 답안 행의 높이
  const HEADER_HEIGHT = 30; // 헤더 행의 높이
  const AVAILABLE_HEIGHT = 205; // mm 단위의 사용 가능한 높이
  const MM_TO_PX = 3.779527559055118; // 1mm = 3.7795px (96dpi 기준)
  const AVAILABLE_HEIGHT_PX = AVAILABLE_HEIGHT * MM_TO_PX;

  // 헤더를 제외하고 실제 답안이 들어갈 수 있는 최대 행 수
  const MAX_ROWS_PER_PAGE = Math.floor((AVAILABLE_HEIGHT_PX - HEADER_HEIGHT) / ANSWER_ROW_HEIGHT);

  // 정답지 페이지 계산
  const ROWS_PER_PAGE = 25; // 한 페이지당 표시할 정답 수 고정

  // 프린트용 HTML 생성 함수 (handlePrint와 handleDownloadPdf에서 공통으로 사용)
  const generatePrintHtml = React.useCallback(() => {
    const printContent = document.querySelector('.preview-container');
    if (!printContent) {
      return null;
    }

    // 측정 div 숨기기 (clone 없이 직접 innerHTML 사용)
    const hiddenEls = printContent.querySelectorAll('[data-no-print]');
    hiddenEls.forEach(el => { el._prevDisplay = el.style.display; el.style.display = 'none'; });

    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .filter(rule => {
              // 상대 경로(../)가 포함된 @font-face 규칙 제거 (Puppeteer에서 로드 불가)
              if (rule.type === CSSRule.FONT_FACE_RULE) {
                return !rule.cssText.includes('../');
              }
              return true;
            })
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    // innerHTML 캡처 후 측정 div 복원
    const capturedHtml = printContent.innerHTML;
    hiddenEls.forEach(el => { el.style.display = el._prevDisplay || ''; });

    return `
      <html>
        <head>
          <title>Print</title>
          <style>
            ${styles}

            @page {
              size: A4;
              margin: 0;
            }

            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              width: 328mm !important;
              height: 464mm !important;
              font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif !important;
            }

            * {
              font-family: inherit;
            }

            .preview-container {
              width: 328mm !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: none !important;
              zoom: 1 !important;
              position: relative !important;
            }

            .page {
              width: 328mm !important;
              min-height: 464mm !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              position: relative !important;
              page-break-after: always !important;
            }

            .problem-page .problems-container {
              padding: 0 16mm !important;
              display: flex !important;
              flex-direction: row !important;
              gap: 16mm !important;
              justify-content: space-between !important;
              position: relative !important;
            }

            .problem-content *:not(.katex):not(.katex *) {
              font-family: 'Pretendard', sans-serif !important;
              font-weight: 400 !important;
              font-size: 18px !important;
            }

            .katex {
              font-size: 1em !important;
            }

            @media print {
              html, body {
                width: 328mm !important;
                height: 464mm !important;
              }
              .preview-container {
                width: 328mm !important;
              }
              .no-print {
                display: none !important;
              }
            }

            /* 풀이 콘텐츠 스타일 (인쇄/PDF용) */
            .solution-content img {
              max-width: 100% !important;
              height: auto !important;
            }
            .solution-content .katex-display {
              max-width: 100% !important;
            }
            .solution-content .katex .base {
              flex-wrap: wrap !important;
            }
            .solution-content .PlaygroundEditorTheme__layoutContainer {
              display: block !important;
              max-width: 100% !important;
            }
            .solution-content .PlaygroundEditorTheme__layoutItem {
              width: 100% !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }
            .solution-content p {
              overflow-wrap: break-word !important;
              word-break: break-word !important;
            }

            /* 풀이 페이지 column-fill:auto 강제 */
            .solution-page [style*="column-count"] {
              column-fill: auto !important;
              -webkit-column-fill: auto !important;
            }
          </style>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css">
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/katex.min.css">
        </head>
        <body>
          ${capturedHtml}
          <script>
            (async () => {
              const warnings = [];
              const READY_FORCE_TIMEOUT_MS = 8000;
              const FONTS_TIMEOUT_MS = 3000;
              const IMAGES_TIMEOUT_MS = 4000;

              const setReady = () => {
                if (warnings.length > 0) {
                  window.__PDF_READY_WARN__ = warnings.join(',');
                }
                window.__PDF_READY__ = true;
              };

              // 어떤 이유로든 대기가 길어지면 일정 시간 후 강제로 ready 마커를 세팅
              const forceTimer = setTimeout(() => {
                warnings.push('ready_forced_timeout=' + READY_FORCE_TIMEOUT_MS);
                setReady();
              }, READY_FORCE_TIMEOUT_MS);

              // 1) 웹폰트 로딩 완료 대기
              try {
                if (document.fonts && document.fonts.ready) {
                  const fontsOk = await Promise.race([
                    document.fonts.ready.then(() => true).catch(() => false),
                    new Promise((res) => setTimeout(() => res(false), FONTS_TIMEOUT_MS)),
                  ]);
                  if (!fontsOk) {
                    warnings.push('fonts_timeout=' + FONTS_TIMEOUT_MS);
                  }
                }
              } catch (e) {
                warnings.push('fonts_error');
              }

              // 2) 이미지 로딩 완료 대기
              try {
                const imgs = Array.from(document.images || []);
                let failedImages = 0;

                const waitImages = Promise.allSettled(
                  imgs.map((img) =>
                    img.complete
                      ? Promise.resolve()
                      : new Promise((res) => {
                          img.addEventListener("load", res, { once: true });
                          img.addEventListener(
                            "error",
                            () => {
                              failedImages += 1;
                              res();
                            },
                            { once: true }
                          );
                        })
                  )
                );

                const imagesOk = await Promise.race([
                  waitImages.then(() => true).catch(() => false),
                  new Promise((res) => setTimeout(() => res(false), IMAGES_TIMEOUT_MS)),
                ]);

                if (!imagesOk) {
                  warnings.push('images_timeout=' + IMAGES_TIMEOUT_MS);
                }
                if (failedImages > 0) {
                  warnings.push('images_failed=' + failedImages);
                }
              } catch (e) {
                warnings.push('images_error');
              }

              clearTimeout(forceTimer);

              // 3) PDF 준비 완료 마커 설정
              setReady();
            })();
          </script>
        </body>
      </html>
    `;
  }, []);

  // 인쇄 함수 정의
  const handlePrint = React.useCallback(() => {
    try {
      const html = generatePrintHtml();
      if (!html) {
        console.error('Print content not found');
        alert('인쇄할 내용을 찾을 수 없습니다. 페이지를 새로고침 후 다시 시도해주세요.');
        return;
      }

      const printWindow = window.open('', '', 'width=800,height=600');
      if (!printWindow) {
        console.error('Could not open print window');
        alert('인쇄 창을 열 수 없습니다. 브라우저의 팝업 차단을 해제해주세요.');
        return;
      }

      try {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
      } catch (writeError) {
        console.error('Print window write failed:', writeError);
        alert('인쇄 내용을 준비하는 중 오류가 발생했습니다. 다시 시도해주세요.');
        try { printWindow.close(); } catch (e) { /* ignore */ }
        return;
      }

      setTimeout(() => {
        try {
          printWindow.print();
        } catch (printError) {
          console.error('Print failed:', printError);
          alert('인쇄 실행 중 오류가 발생했습니다. 브라우저 설정을 확인해주세요.');
        } finally {
          try { printWindow.close(); } catch (e) { /* ignore */ }
        }
      }, 3000);
    } catch (error) {
      console.error('Print error:', error);
      alert('인쇄 중 예상치 못한 오류가 발생했습니다. 페이지를 새로고침 후 다시 시도해주세요.');
    }
  }, [generatePrintHtml]);

  // 인쇄 함수를 부모 컴포넌트로 전달만 하고, 실행은 하지 않음
  React.useEffect(() => {
    if (onPrint) {
      onPrint(() => handlePrint);
    }
  }, [handlePrint, onPrint]);

  // HTML 생성 함수를 부모 컴포넌트로 전달
  React.useEffect(() => {
    if (onGetPrintHtml) {
      onGetPrintHtml(() => generatePrintHtml);
    }
  }, [generatePrintHtml, onGetPrintHtml]);

  const wrapperStyle = mode === 'preview' ? {
    margin: '0 auto',
    width: 'fit-content',
  } : {};

  return (
    <div style={wrapperStyle}>
      <style>
        {`
          @media screen {
      body { background: gray; }
      .pdf-container {
        width: 328mm;
        margin: 0 auto;
        background: white;
        box-shadow: 0 0 10px rgba(0,0,0,0.3);
      }
    }
    .print-header-first { 
        margin: 0;
        height: 120mm;
        background-image: url('${process.env.PUBLIC_URL}/assets/images/bg_test.png');
        background-size: contain;
        background-position: top center;
        background-repeat: no-repeat;
        position: relative;
    }
    
    .print-header-rest {
        height: 15mm;
        background: none;
        margin: 0;
        position: relative;
    }

    .header-content {
      position: absolute;
      top: 16mm;
      left: 16mm;
      right: 16mm;
      z-index: 1;
    }
    .header-grid {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 20px;
    }
    .header-left .subject {
      font-size: 14pt;
      color: #333;  // 가독성을 위해 색상 조정
      margin: 0;
      font-weight: 500;
    }
    .header-left .chapter {
      font-size: 32pt;
      font-weight: 900;
      margin: 8mm 0;  // 여백 추가
      color: #4ABC85;
    }
    .header-left .scope {
      font-size: 10pt;
      color: gray;
      margin: 0;
    }
    .header-right {
      text-align: right;
      position: relative;
    }
    .test-number {
      font-size: 14pt;
      margin: 0;
      // position: absolute;
      top: 5mm;
      right: 68mm;
    }
    .student-name {
      font-size: 18pt;
      font-weight: bold;
      margin-top: 65mm;
      color: #000;
    }
    .problem-number {
      font-size: 36px;
      font-weight: 900;
      min-width: 36px;
      white-space: nowrap;
      color: #4ABC85;
      margin: 0;
      line-height: 1;
      position: relative;
    }
    .editor-paragraph {
      position: relative;
    }
    .editor-text-bold {
      font-weight: bold;
    }
    .editor-text-italic {
      font-style: italic;
    }
    .editor-text-underline {
      text-decoration: underline;
    }
    .PlaygroundEditorTheme__paragraph {
      margin: 0;
      position: relative;
    }
    .PlaygroundEditorTheme__text-bold {
      font-weight: bold;
    }
    .page {
      width: 328mm;
      min-height: 464mm;
      background: white;
      box-sizing: border-box;
      padding: 0;
      position: relative;
      margin-bottom: 10px;
    }
    .problem-page .problems-container {
      padding: 0 16mm;
      display: flex;
      flex-direction: row;
      gap: 16mm;
      min-height: 320.5mm;
      justify-content: space-between;
      position: relative;
    }
    .problem-page .problems-container::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      height: 100%;
      width: 1px;
      background-color: #ccc;
      transform: translateX(-50%);
    }
    .problem-page .problems-column {
      flex: 1;
      position: relative;
      display: block;
      width: calc(50% - 8mm);
      min-height: 320.5mm;
      overflow: visible;
    }
    .problem-page .problem-item {
      width: 100%;
      max-width: 488px;
      overflow-wrap: break-word;
      text-align: left;
      display: flex;
      gap: 28px;
      align-items: flex-start;
      position: absolute;
      left: 0;
    }
    .problem-page .problem-content .PlaygroundEditorTheme__layoutContainer {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16mm;
      max-width: 100%;
      margin: 8mm 0;
      page-break-inside: avoid;
    }
    .problem-page .problem-content .PlaygroundEditorTheme__layoutItem {
      width: 100%;
      min-width: 0;
      padding: 0;
      box-sizing: border-box;
      page-break-inside: avoid;
      overflow-wrap: break-word;
      border: none !important;
    }
    .problem-page .problem-content img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 8mm auto;
    }
    .problem-page .problem-content .math-element {
      font-size: 0.91em;
      display: inline-block;
      vertical-align: middle;
      margin: 0 2px;
    }
    .problem-page:not(:first-child) .problems-container {
      margin-top: 30mm;
    }
    .PlaygroundEditorTheme__layoutItem > * {
      margin: 2mm 0;
    }
    .PlaygroundEditorTheme__imageContainer {
      text-align: center;
      margin: 8mm 0;
    }
    .PlaygroundEditorTheme__image {
      display: inline-block;
      max-width: 100%;
      vertical-align: middle;
    }
    .print-footer {
      position: absolute;
      bottom: 10mm;
      left: 16mm;
      right: 16mm;
      padding-bottom: 0;
    }
    .problem-page .problem-content [data-lexical-layout-container] {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(3, auto);
      max-width: 100%;
    }
    .PlaygroundEditorTheme__boxContent {
      border: 1px solid black;
      padding: 10px;
      margin: 10px auto;
      max-width: 100%;
      border-radius: 4px;
      background-color: #ffffff;
      page-break-inside: avoid;
    }
    .problem-page .problem-content {
      margin: 0;
      width: 100%;
      overflow-wrap: break-word;
      word-break: break-word;
    }
    .problem-page .problem-item[data-full-column="true"] {
        height: 150mm;
        z-index: 1;
    }
    /* 첫 페이지 규칙 */
    div.problem-page.first-child .problem-item[data-area="top"] {
        top: 0 !important;
    }
    div.problem-page.first-child .problem-item[data-area="bottom"] {
        top: 160mm !important;
    }

    /* 두 번째 페이지 이후 규칙 */
    div.problem-page:not(.first-child) .problem-item[data-area="top"] {
        top: 0mm !important;
    }
    div.problem-page:not(.first-child) .problem-item[data-area="bottom"] {
        top: 205mm !important;
    }
    /* 첫 페이지의 구분선 */
    .problem-page.first-child .problems-container::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      height: 319mm;  // footer까지 내려가도록 높이 증가
      width: 1px;
      background-color: #ccc;
      transform: translateX(-50%);
    }

    /* 두 번째 페이지 이후의 구분선 */
    .problem-page:not(.first-child) .problems-container::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      height: 409mm;  // 문제 영역 높이만큼만
      width: 1px;
      background-color: #ccc;
      transform: translateX(-50%);
    }
    @media print {
        .print-header-first {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background-image: url('${process.env.PUBLIC_URL}/assets/images/bg_test.png') !important;
        }
        .page-break {
            display: none;
        }
        .page {
            margin-bottom: 0;
            box-shadow: none;
            page-break-after: always;  /* 각 페이지 후에 강제 페이지 나눔 */
            page-break-inside: avoid;  /* 페이지 내부 콘텐츠 분리 방지 */
        }
    }

    /* 첫 페이지의 problems-container 높이 (330mm = 1247.24px) */
    .problem-page.first-child .problems-container {
        height: 320.5mm !important;
        min-height: 320.5mm !important;
        max-height: 320.5mm !important;
        margin: 0 !important;
        padding: 0 60.4724px !important;
    }

    /* 두 번째 페이지 이후의 problems-container 높이 */
    .problem-page:not(.first-child) .problems-container {
        height: 410.5mm !important;
        min-height: 410.5mm !important;
        max-height: 410.5mm !important;
        margin-top: 57px !important;
        padding: 0 60.4724px !important;
    }

    /* 문제 페이지 공통 스타일 */
    .problem-page .problems-container {
        display: flex !important;
        flex-direction: row !important;
        justify-content: space-between !important;
        gap: 60.4724px !important;
        position: relative !important;
        box-sizing: border-box !important;
    }

    /* 첫 페이지 푸터 */
    .problem-page.first-child .print-footer {
        position: absolute;
        bottom: 10mm;
        left: 16mm;
        right: 16mm;
        padding-bottom: 0;
    }

    /* 두 번째 페이지부터의 푸터 */
    .problem-page:not(.first-child) .print-footer {
        position: absolute;
        bottom: 10mm;  /* 10mm에서 5mm로 변경 */
        left: 16mm;
        right: 16mm;
        padding-bottom: 0;
    }
        `}
      </style>
      <div className="preview-container" style={containerStyle}>
        <style>{`
          .problem-content *:not(.katex):not(.katex *) {
            font-family: 'Pretendard', sans-serif !important;
            font-weight: 400 !important;
            font-size: 18px !important;
          }
          .katex {
            font-size: 1em !important;
          }
          .test-number {
            right: 70mm;
            height: 30px;
            line-height: 30px;
            margin: 0;
            display: flex;
            align-items: center;
            white-space: nowrap;
          }
          .student-name {
            white-space: nowrap;
          }
          .header-grid {
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
          }
          /* 풀이 콘텐츠 스타일 */
          .solution-content img {
            max-width: 100% !important;
            height: auto !important;
          }
          .solution-content .katex-display {
            max-width: 100% !important;
          }
          .solution-content .PlaygroundEditorTheme__layoutContainer {
            display: block !important;
            max-width: 100% !important;
          }
          .solution-content .PlaygroundEditorTheme__layoutItem {
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }
          .solution-content p {
            margin: 2px 0 !important;
            overflow-wrap: break-word !important;
            word-break: break-word !important;
          }
          /* 풀이 페이지 column-fill:auto 강제 */
          .solution-page [style*="column-count"] {
            column-fill: auto !important;
            -webkit-column-fill: auto !important;
          }
        `}</style>
        {combinedPages.map((page, pageIndex) => (
          <div key={`page-${pageIndex}`} className={`page problem-page ${pageIndex === 0 ? 'first-child' : ''}`}>
            {page.type === 'content' ? (
              <>
                {/* Header */}
                {pageIndex === 0 ? (
                  <div className="print-header-first">
                    <div className="header-content">
                      <div className="header-grid">
                        <div className="header-left">
                          <p className="subject" style={{ fontFamily: "'Pretendard', sans-serif" }}>{edu_ver}</p>
                          <p className="chapter" style={{ color: '#4ABC85', fontWeight: '900', fontFamily: "'Pretendard', sans-serif" }}>{tree_name}</p>
                        </div>
                        <div className="header-right">
                          <p className="test-number" style={{ fontFamily: "'Pretendard', sans-serif" }}>TID. {exam_id}</p>
                          {student_name && <p className="student-name" style={{ fontFamily: "'Pretendard', sans-serif" }}>{student_name}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="print-header-rest">
                    <div className="header-content"></div>
                  </div>
                )}

                {/* Problems Container */}
                <div className="problems-container">
                  <div className="problems-column">
                    {page.content.left?.map((problem, index) => (
                      <ProblemItem
                        key={problem.problem_id}
                        problem={problem}
                        index={index}
                        mode={mode}
                        onClick={onProblemClick}
                        editingProblemId={editingProblemId}
                      />
                    ))}
                  </div>
                  <div className="problems-column">
                    {page.content.right?.map((problem, index) => (
                      <ProblemItem
                        key={problem.problem_id}
                        problem={problem}
                        index={index}
                        mode={mode}
                        onClick={onProblemClick}
                        editingProblemId={editingProblemId}
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* 빈 A4 페이지 (양면인쇄 간지) - 양식 없음 */
              null
            )}

            {/* Footer - content 페이지에만 */}
            {page.type === 'content' && <div className="print-footer" style={{
              position: 'absolute',
              bottom: '10mm',
              left: 0,
              right: 0,
              width: '100%',
              height: '15mm',
              padding: '0 15mm',
              boxSizing: 'border-box'
            }}>
              <hr style={{
                border: 'none',
                borderTop: '1px solid #ccc',
                margin: '0 0 5mm 0',
                width: '100%'
              }} />
              <div style={{
                position: 'relative',
                height: '8mm',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#666',
                  fontSize: '10pt',
                  width: '100%',
                  fontFamily: "'Pretendard', sans-serif"
                }}>
                  <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>{leftFooter}</span>
                  <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>{edu_ver} · {tree_name}</span>
                </div>
                <span style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '18pt',
                  fontWeight: 'bold',
                  lineHeight: '1',
                  fontFamily: "'Pretendard', sans-serif"
                }}>
                  {String(pageIndex + 1).padStart(2, '0')}
                </span>
              </div>
            </div>}
          </div>
        ))}

        {answers.length > 0 && (
          <>
            {Array.from({ length: Math.ceil(answers.length / ROWS_PER_PAGE) }).map((_, pageIndex) => (
              <div key={pageIndex} className={`page ${pages.length + pageIndex === 0 ? 'first-child' : ''}`} style={{
                position: 'relative',
                width: '1239.69px',
                height: '297mm',
                pageBreakAfter: 'always'
              }}>
                <div style={{
                  ...answerPageStyles.printHeaderRest,
                  width: '100%',
                  padding: '30px 15mm 0 15mm'
                }}>
                  <div style={answerPageStyles.headerContent}>
                    <div style={answerPageStyles.topRow}>
                      <p style={{ ...answerPageStyles.examId, fontFamily: "'Pretendard', sans-serif" }}>TID. {exam_id}</p>
                      <div style={answerPageStyles.logo} />
                    </div>
                    <div style={answerPageStyles.bottomRow}>
                      <h1 style={{ ...answerPageStyles.title, fontFamily: "'Pretendard', sans-serif" }}>빠른정답</h1>
                      <div>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          marginRight: '4px',
                          fontFamily: "'Pretendard', sans-serif"
                        }}>{student_name}</span>
                        <span style={{
                          fontSize: '14px',
                          color: '#666',
                          fontFamily: "'Pretendard', sans-serif"
                        }}>{edu_ver} · {tree_name}</span>
                      </div>
                    </div>
                    <hr style={answerPageStyles.headerLine} />
                  </div>
                </div>

                <div style={{
                  ...answerPageStyles.answerContainer,
                  width: '100%',
                  padding: '0 15mm',
                  minHeight: '205mm',
                  overflow: 'visible'
                }}>
                  <table style={{
                    ...answerPageStyles.answersTable,
                    width: '100%',
                    height: 'auto',
                    maxHeight: 'none'
                  }}>
                    <thead>
                      <tr style={answerPageStyles.tableRow}>
                        <th style={{ ...answerPageStyles.tableHeader, width: '80px', textAlign: 'left', paddingLeft: '0', fontFamily: "'Pretendard', sans-serif" }}>문제 번호</th>
                        <th style={{ ...answerPageStyles.tableHeader, width: '80px', fontFamily: "'Pretendard', sans-serif" }}>PID</th>
                        <th style={{ ...answerPageStyles.tableHeader, fontFamily: "'Pretendard', sans-serif" }}>정답</th>
                      </tr>
                    </thead>
                    <tbody>
                      {answers.slice(pageIndex * ROWS_PER_PAGE, (pageIndex + 1) * ROWS_PER_PAGE).map((answer, index) => (
                        <tr key={answer.problem_id} style={{
                          ...answerPageStyles.tableRow,
                          borderBottom: index === answers.length - 1 ? 'none' : '1px solid #E5E5E5'
                        }}>
                          <td style={{ 
                            ...answerPageStyles.tableCell, 
                            ...answerPageStyles.numberCell,
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                            colorAdjust: 'exact'
                          }}>
                            {String(answer.number).padStart(2, '0')}
                          </td>
                          <td style={{ 
                            ...answerPageStyles.tableCell, 
                            ...answerPageStyles.tidCell 
                          }}>
                            {answer.problem_id}
                          </td>
                          <td style={{ 
                            ...answerPageStyles.tableCell, 
                            ...answerPageStyles.answerCell 
                          }}>
                            <div 
                              style={{ 
                                margin: 0, 
                                whiteSpace: 'nowrap',
                                fontFamily: 'Pretendard, sans-serif',
                                fontSize: '18px',
                                fontWeight: '500'
                              }}
                              dangerouslySetInnerHTML={{ __html: answer.answer }} 
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="print-footer" style={{
                  position: 'absolute',
                  bottom: '10mm',
                  left: 0,
                  right: 0,
                  width: '100%',
                  height: '15mm',
                  padding: '0 15mm',
                  boxSizing: 'border-box'
                }}>
                  <hr style={{ 
                    border: 'none', 
                    borderTop: '1px solid #ccc', 
                    margin: '0 0 5mm 0',
                    width: '100%'
                  }} />
                  <div style={{
                    position: 'relative',
                    height: '8mm',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      color: '#666',
                      fontSize: '10pt',
                      width: '100%',
                      fontFamily: "'Pretendard', sans-serif"
                    }}>
                      <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>{leftFooter}</span>
                      <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>{edu_ver} · {tree_name}</span>
                    </div>
                    <span style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '18pt',
                      fontWeight: 'bold',
                      lineHeight: '1',
                      fontFamily: "'Pretendard', sans-serif"
                    }}>
                      {String(pageIndex + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* 풀이 측정용 - 1열(533px) 세로 배치, preview-container 안 (CSS 적용) */}
        {solutions.length > 0 && (
          <div
            ref={solMeasureRef}
            data-no-print="true"
            aria-hidden="true"
            style={{
              position: 'absolute', visibility: 'hidden', left: '-9999px', top: 0,
              width: '533px', boxSizing: 'border-box'
            }}
          >
            {solutions.map((sol, idx) => (
              <div key={`m-${sol.problem_id}`} data-sol-idx={idx} style={{
                marginBottom: '3mm', paddingBottom: '2mm', borderBottom: '1px solid #eee'
              }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{
                    fontSize: '20px', fontWeight: 900, minWidth: '32px',
                    flexShrink: 0, whiteSpace: 'nowrap', lineHeight: '1.4'
                  }}>{String(sol.number).padStart(2, '0')}</span>
                  {sol.content ? (
                    <div className="problem-content solution-content"
                      style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: sol.content }} />
                  ) : (
                    <span style={{ color: '#999', fontSize: '14px', lineHeight: '1.4' }}>풀이 없음</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 풀이 페이지 - 1열 배치를 슬라이스하여 좌/우 칼럼으로 분배 */}
        {solLayout.pages.map((cols, pidx) => (
          <div key={`sol-page-${pidx}`} className="page solution-page" style={{
            position: 'relative', width: '1239.69px', height: '297mm',
            pageBreakAfter: 'always', overflow: 'hidden'
          }}>
            {/* 헤더 */}
            <div style={{ ...answerPageStyles.printHeaderRest, width: '100%', padding: '30px 15mm 0 15mm' }}>
              <div style={answerPageStyles.headerContent}>
                <div style={answerPageStyles.topRow}>
                  <p style={{ ...answerPageStyles.examId, fontFamily: "'Pretendard', sans-serif" }}>TID. {exam_id}</p>
                  <div style={answerPageStyles.logo} />
                </div>
                <div style={answerPageStyles.bottomRow}>
                  <h1 style={{ ...answerPageStyles.title, fontFamily: "'Pretendard', sans-serif" }}>풀이</h1>
                  <div>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', marginRight: '4px', fontFamily: "'Pretendard', sans-serif" }}>{student_name}</span>
                    <span style={{ fontSize: '14px', color: '#666', fontFamily: "'Pretendard', sans-serif" }}>{edu_ver} · {tree_name}</span>
                  </div>
                </div>
                <hr style={answerPageStyles.headerLine} />
              </div>
            </div>

            {/* 본문: 좌/우 칼럼 - 각 칼럼은 전체 스트립의 슬라이스 */}
            <div ref={pidx === 0 ? solContentRef : undefined} style={{
              padding: '0 15mm', display: 'flex', gap: '16mm',
              height: '370mm', overflow: 'hidden', boxSizing: 'border-box',
              position: 'relative'
            }}>
              {/* 세로 구분선 */}
              <div style={{
                position: 'absolute', top: 0, left: '50%',
                height: '100%', width: '1px', backgroundColor: '#ccc',
                transform: 'translateX(-50%)', zIndex: 1
              }} />
              {cols.map((col, colIdx) => (
                <div key={`col-${colIdx}`} ref={pidx === 0 && colIdx === 0 ? solColRef : undefined} style={{
                  flex: 1, position: 'relative', overflow: 'hidden', height: '100%'
                }}>
                  {col.visible.map(({ sol, y }) => (
                    <div key={`sc-${colIdx}-${sol.problem_id}`} style={{
                      position: 'absolute',
                      top: `${y - col.start}px`,
                      left: 0, right: 0
                    }}>
                      <div style={{
                        marginBottom: '3mm', paddingBottom: '2mm', borderBottom: '1px solid #eee'
                      }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <span style={{
                            fontSize: '20px', fontWeight: 900, color: '#4ABC85', minWidth: '32px',
                            flexShrink: 0, whiteSpace: 'nowrap', lineHeight: '1.4', fontFamily: "'Pretendard', sans-serif"
                          }}>{String(sol.number).padStart(2, '0')}</span>
                          {sol.content ? (
                            <div className="problem-content solution-content"
                              style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                              dangerouslySetInnerHTML={{ __html: sol.content }} />
                          ) : (
                            <span style={{ color: '#999', fontSize: '14px', lineHeight: '1.4', fontFamily: "'Pretendard', sans-serif" }}>풀이 없음</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* 상단 흰색 그라데이션: 이전 칼럼에서 이어지는 잘린 라인 가림 */}
                  {!(pidx === 0 && colIdx === 0) && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: '20px',
                      background: 'linear-gradient(white, transparent)', pointerEvents: 'none', zIndex: 2
                    }} />
                  )}
                  {/* 하단 흰색 그라데이션: 잘린 라인 가림 */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '20px',
                    background: 'linear-gradient(transparent, white)', pointerEvents: 'none', zIndex: 2
                  }} />
                </div>
              ))}
            </div>

            {/* 푸터 */}
            <div className="print-footer" style={{
              position: 'absolute', bottom: '10mm', left: 0, right: 0,
              width: '100%', height: '15mm', padding: '0 15mm', boxSizing: 'border-box'
            }}>
              <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '0 0 5mm 0', width: '100%' }} />
              <div style={{ position: 'relative', height: '8mm', display: 'flex', alignItems: 'center' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  color: '#666', fontSize: '10pt', width: '100%', fontFamily: "'Pretendard', sans-serif"
                }}>
                  <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>{leftFooter}</span>
                  <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>{edu_ver} · {tree_name}</span>
                </div>
                <span style={{
                  position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                  fontSize: '18pt', fontWeight: 'bold', lineHeight: '1', fontFamily: "'Pretendard', sans-serif"
                }}>{String(pidx + 1).padStart(2, '0')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ProblemItem 컴포넌트
const ProblemItem = ({ problem, index, mode, onClick, editingProblemId = null }) => {
  // 현재 문제가 수정 중인 문제인지 확인
  const isEditing = editingProblemId && editingProblemId === problem.problem_id;
  
  return (
    <div 
      className="problem-item"
      data-area={index === 0 ? "top" : "bottom"}
      data-problem-id={problem.problem_id}
      data-problem-number={problem.problemNumber}
      style={{
        top: `${problem.startHeight}mm`,
        cursor: mode === 'preview' && onClick ? 'pointer' : 'default',
        // 수정 중인 문제에 녹색 테두리 적용
        border: isEditing ? '3px solid #22c55e' : 'none',
        borderRadius: isEditing ? '8px' : '0',
        backgroundColor: isEditing ? '#f0fdf4' : 'transparent',
        padding: isEditing ? '8px' : '0',
        margin: isEditing ? '4px' : '0',
        transition: 'all 0.2s ease-in-out'
      }}
      onClick={() => mode === 'preview' && onClick && onClick(problem)}
    >
      <div className="problem-number" style={{ fontFamily: "'Pretendard', sans-serif" }}>
        {String(problem.problemNumber).padStart(2, '0')}
      </div>
      <div 
        className="problem-content"
        dangerouslySetInnerHTML={{ __html: problem.content }}
      />
    </div>
  );
};

export default PrintPreview;
